import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { twMerge } from "tailwind-merge";
import Button from "../Button/Button";
import { useId } from "../../../hooks/useId";
import Divider from "../Divider/Divider";
import Checkbox from "../Checkbox";
import Tooltip from "../Tooltip/Tooltip";

export default function Select({
  btnInitialColor = "gray100",
  btnSelectedColor = "primary50",
  buttonProps = {},
  className,
  direction = "down",
  disabled,
  header,
  menuClassName = "w-full",
  onChange,
  options = [],
  placeholder = "Select…",
  renderOption,
  renderSelected,
  showSelectedText = true,
  value,
  withCheckbox = false,
  withSearch = false,
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const selectedOption = useMemo(() => {
    return options.find((o) => o.value === value) || null;
  }, [options, value]);

  const selectedText = useMemo(() => {
    if (!selectedOption) return undefined;
    return selectedOption.text;
  }, [selectedOption]);

  const filteredOptions = useMemo(() => {
    try {
      if (!withSearch) return options;

      const search = searchValue.trim().toLowerCase();
      if (!search) return options;

      return options.filter((opt) => {
        const candidates = [opt.text]
          .filter((candidate) => typeof candidate === "string")
          .map((candidate) => candidate.toLowerCase());

        return candidates.some((candidate) => candidate.includes(search));
      });
    } catch (error) {
      console.error("Error filtering options:", error);
      return [];
    }
  }, [options, searchValue, withSearch]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Ensure the selected option is visible when opening
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[aria-selected="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const buttonContent =
    showSelectedText && selectedOption
      ? typeof renderSelected === "function"
        ? renderSelected(selectedOption, open)
        : selectedText
      : placeholder;

  return (
    <div className={twMerge("relative", className)} ref={rootRef}>
      <Button
        className="flex w-full flex-row items-center gap-2"
        color={
          value !== undefined && value !== null
            ? btnSelectedColor
            : btnInitialColor
        }
        isDisabled={disabled}
        onClick={() => setOpen((o) => !o)}
        ref={buttonRef}
        rounded="full"
        size="sm"
        {...buttonProps}
      >
        {buttonContent}

        <FiChevronDown
          className={twMerge(
            "shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
      </Button>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className={twMerge(
              "absolute z-10 w-full",
              direction === "up" ? "bottom-full mb-1" : "top-full mt-1"
            )}
            exit={{ opacity: 0, scale: 0.98, y: direction === "up" ? 4 : -4 }}
            initial={{
              opacity: 0,
              scale: 0.98,
              y: direction === "up" ? 4 : -4,
            }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            <div
              className={twMerge(
                menuClassName,
                "border-gray200 flex flex-col overflow-hidden rounded-md border bg-white shadow-lg"
              )}
            >
              {header && (
                <>
                  <div className="px-4 py-3 text-base font-semibold text-black">
                    {header}
                  </div>
                  <Divider color="gray200" type="horizontal" />
                </>
              )}

              {withSearch && (
                <div className="px-4 py-3">
                  <input
                    className={twMerge(
                      "border-gray300 h-9 w-full rounded-md border px-4 text-sm outline-none",
                      "focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
                    )}
                    name="search"
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search"
                    value={searchValue}
                  />
                </div>
              )}

              <Divider color="gray200" type="horizontal" />

              <div
                className="min-h-0 flex-1 overflow-y-auto"
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
              >
                {!filteredOptions.length ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-gray700 text-sm">No results</div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 px-1.5 py-1">
                    {filteredOptions.map((opt) => {
                      const isChecked = opt.value === value;
                      const isDisabled = Boolean(opt.disabled);

                      const defaultOptionContent = (
                        <>
                          {withCheckbox ? (
                            <Checkbox isChecked={isChecked} />
                          ) : null}
                          <span className="truncate text-left text-sm">
                            {opt.text}
                          </span>
                        </>
                      );

                      const optionContent =
                        typeof renderOption === "function"
                          ? renderOption(opt, { isChecked, isDisabled })
                          : defaultOptionContent;

                      const btn = (
                        <button
                          aria-disabled={isDisabled}
                          aria-selected={isChecked}
                          className={twMerge(
                            "flex w-full items-center gap-2 rounded-sm p-2.5",
                            isDisabled
                              ? "cursor-not-allowed opacity-60"
                              : "hover:bg-gray100 focus:bg-gray100 cursor-pointer focus:outline-none",
                            isChecked &&
                              !withCheckbox &&
                              "bg-gray50 font-medium"
                          )}
                          data-active={isChecked ? "true" : undefined}
                          disabled={isDisabled}
                          key={String(opt.value)}
                          onClick={() =>
                            !isDisabled ? handleSelect(opt.value) : null
                          }
                          role="option"
                          type="button"
                        >
                          {optionContent}
                        </button>
                      );

                      return isDisabled && opt.tooltip ? (
                        <Tooltip content={opt.tooltip} key={String(opt.value)}>
                          <span className="block">{btn}</span>
                        </Tooltip>
                      ) : (
                        btn
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
